import React, { Component } from 'react';
import { GlobalHotKeys } from 'react-hotkeys';
import { Vector, EmptyLinkedList, LinkedList } from 'prelude-ts';

import { levels, Level } from './Levels';
import { Storage, MetricTracker, LevelCompleteMessage } from './Tracking';
import Statement from './Statement';
import History from './History';
import MenuBar from './components/MenuBar';
import PlayArea from './components/PlayArea';
import StatusBar from './components/StatusBar';
import WinMessage from './components/WinMessage';

import styles from './styles/Game.module.css';
import BeginMessage from './components/BeginMessage';
import ProofTree from './components/ProofTree';
import { Rule, DefineRule, RuleApplication } from './Rule';

interface Props {}

/**
 * The game can be in one of many stages, and the available properties
 * depend on which stage the game is in. TypeScript allows us to essentially
 * implement stages as a state machine. The `stage` property (which is
 * just a string) determines what properties are available.
 */
type State = BeginState | SolvingState | LevelCompleteState;

/**
 * Properties common to all game stages.
 */
interface CommonState {
  levelNumber: number;
  level: Level;
  tooltip: boolean;
}

/**
 * Properties relevant when a game has just started.
 */
interface BeginState extends CommonState {
  stage: 'begin';
}

/**
 * Properties relevant when playing a level.
 */
interface SolvingState extends CommonState {
  stage: 'solving';
  history: History<RuleApplication>;
}

/**
 * Properties relevant when a level has been completed.
 */
interface LevelCompleteState extends CommonState {
  stage: 'levelComplete';
  userMoves: number;
  proof: LinkedList<RuleApplication>;
}

class Game extends Component<Props, State> {
  /* Maximum level number */
  constructor(props: Props) {
    super(props);
    const startingLevel = Storage.getStartingLevel();
    this.state = {
      stage: 'begin',
      levelNumber: startingLevel,
      level: levels.get(startingLevel).getOrThrow(),
      tooltip: levels.get(startingLevel).getOrThrow().tooltip
    };
  }

  private readonly tracker: MetricTracker = new MetricTracker();

  /** Starts the game after the initial pop up tutorial. */
  startGame = () => {
    Storage.giveConsent();
    this.gotoLevel(Storage.getStartingLevel());
  };

  /**
   * Set the current level according to the given level index.
   *
   * @param levelNumber index of the level in `levels`
   */
  gotoLevel = (levelNumber: number) => {
    const level = levels.get(levelNumber).getOrThrow();
    const first = new RuleApplication(
      new DefineRule(),
      new Statement(Vector.empty(), Vector.empty()),
      Vector.of(level.statement.get()),
      Vector.of(level.statement.get())
    );
    this.setState({
      stage: 'solving',
      levelNumber: levelNumber,
      level: level,
      history: History.of(first),
      tooltip: level.tooltip
    });
  };

  /** Changes all subgoals currently being proven to the given input.
   *
   * Correctly handles stale messages, undo/redo, and level completion.
   * Therefore, this function should be used to send all "set" messages
   * to the history.
   *
   *  @param newSubgoals the new value of history
   */
  updateSubgoals = (
    rule: Rule,
    to: Statement,
    appResult: Vector<Statement>,
    newSubgoals: Vector<Statement>
  ) => {
    // Remove fully solved subgoals.
    newSubgoals = newSubgoals.filter(s => !s.isSolved());
    const ruleApplication = new RuleApplication(
      rule,
      to,
      appResult,
      newSubgoals
    );
    this.setState(state => {
      if (state.stage !== 'solving') {
        return state;
      }
      if (newSubgoals.isEmpty()) {
        const nextLevel =
          state.levelNumber + 1 >= levels.length() ? 0 : state.levelNumber + 1;
        Storage.setStartingLevel(nextLevel);
        const lvlCompleteMessage: LevelCompleteMessage = {
          uid: Storage.getUUID(),
          levelName: state.level.name,
          levelNum: state.levelNumber,
          movesRequired: state.level.idealMoves,
          movesUsed: state.history.length() + 1
        };
        this.tracker.sendMessage(lvlCompleteMessage);
        return {
          stage: 'levelComplete',
          levelNumber: state.levelNumber,
          level: state.level,
          tooltip: false,
          userMoves: state.history.length() + 1,
          proof: state.history.getHistory().prepend(ruleApplication)
        };
      }

      return {
        stage: 'solving',
        levelNumber: state.levelNumber,
        level: state.level,
        tooltip: false,
        history: state.history.setDeduplicate(ruleApplication)
      };
    });
  };

  /**
   * Apply the given action to the specified subgoal.
   */
  applyAction = (subgoal: Statement['id'], rule: Rule) => {
    if (this.state.stage !== 'solving') {
      return;
    }
    const subgoals = this.state.history.get().result;
    const appliedTo = subgoals.find(s => s.id === subgoal).getOrThrow();
    const appResult = rule.apply(appliedTo);
    const result = subgoals.flatMap(s =>
      s.id === subgoal ? appResult : Vector.of(s)
    );
    this.updateSubgoals(rule, appliedTo, appResult, result);
  };

  /** Handles undo action */
  undo = () => {
    this.setState(state => {
      if (state.stage !== 'solving') {
        return state;
      }
      return {
        stage: 'solving',
        levelNumber: state.levelNumber,
        level: state.level,
        tooltip: false,
        history: state.history.undo()
      };
    });
  };

  /** Handles redo action */
  redo = () => {
    this.setState(state => {
      if (state.stage !== 'solving') {
        return state;
      }
      return {
        stage: 'solving',
        levelNumber: state.levelNumber,
        level: state.level,
        tooltip: false,
        history: state.history.redo()
      };
    });
  };

  render() {
    const currentSubgoals: Vector<Statement> =
      this.state.stage === 'solving'
        ? this.state.history.get().result
        : Vector.of(new Statement(Vector.empty(), Vector.empty()));

    const gameOver: boolean =
      this.state.stage === 'levelComplete' &&
      this.state.levelNumber === levels.length() - 1;

    let hasUndo, hasRedo, hasRestart: boolean;
    if (this.state.stage === 'solving') {
      hasUndo = this.state.history.hasUndo();
      hasRedo = this.state.history.hasRedo();
      hasRestart = hasUndo || hasRedo;
    } else {
      hasUndo = false;
      hasRedo = false;
      hasRestart = false;
    }

    const userMoves =
      this.state.stage === 'solving'
        ? this.state.history.length()
        : this.state.stage === 'levelComplete'
        ? this.state.userMoves
        : 0;

    const ruleHistory =
      this.state.stage === 'solving'
        ? this.state.history.getHistory()
        : this.state.stage === 'levelComplete'
        ? this.state.proof
        : new EmptyLinkedList<RuleApplication>();
    return (
      <div className={styles.game}>
        <MenuBar
          levelNumber={this.state.levelNumber}
          undo={this.undo}
          redo={this.redo}
          restart={this.gotoLevel}
          hasUndo={hasUndo}
          hasRedo={hasRedo}
          hasRestart={hasRestart}
        />
        <PlayArea
          subgoals={currentSubgoals}
          executor={this.applyAction}
          draggingEnabled={this.state.level.draggingEnabled}
          tooltip={this.state.tooltip}
        />
        <ProofTree rules={ruleHistory} />
        <StatusBar
          levelNumber={this.state.levelNumber}
          levelTitle={this.state.level.name}
          userMoves={userMoves}
          idealMoves={this.state.level.idealMoves}
        />
        <span className={styles.credit}>
          Icons designed by{' '}
          <a href="https://www.flaticon.com/authors/smashicons">Smashicons</a>{' '}
          and{' '}
          <a href="https://www.flaticon.com/authors/pixel-buddha">
            Pixel Buddha
          </a>{' '}
          from Flaticon
        </span>

        <WinMessage
          open={this.state.stage === 'levelComplete'}
          gameOver={gameOver}
          currentLevel={this.state.levelNumber}
          gotoLevel={this.gotoLevel}
          proof={ruleHistory}
        />
        <BeginMessage
          open={this.state.stage === 'begin'}
          startGame={this.startGame}
        />
        <GlobalHotKeys
          keyMap={Game.debugKeys}
          handlers={this.debugKeyHandler}
        />
      </div>
    );
  }

  /**
   * Immediately solve the current level.
   */
  private debugWin = () => {
    this.setState(state => {
      if (state.stage !== 'solving') {
        return state;
      }
      return {
        stage: 'levelComplete',
        levelNumber: state.levelNumber,
        level: state.level,
        tooltip: false,
        userMoves: state.history.length(),
        proof: state.history.getHistory()
      };
    });
  };

  /**
   * Immediately go to the next level.
   */
  private debugNextLevel = () => {
    if (this.state.levelNumber < levels.length() - 1) {
      this.gotoLevel(this.state.levelNumber + 1);
    }
  };

  /**
   * Immediately go to the previous level.
   */
  private debugPreviousLevel = () => {
    if (0 < this.state.levelNumber) {
      this.gotoLevel(this.state.levelNumber - 1);
    }
  };

  /**
   * Mapping from keys to action names.
   */
  private static debugKeys = {
    win: ['w'],
    previousLevel: ['left'],
    nextLevel: ['right']
  };

  /**
   * Mapping from action names to handlers.
   */
  debugKeyHandler = {
    // win: this.debugWin,
    previousLevel: this.debugPreviousLevel,
    nextLevel: this.debugNextLevel
  };
}

export default Game;
